import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, ButtonBase, IconButton, Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import EmailEditor from 'react-email-editor';
import {
    Editor as SimpleWysiwygEditor, EditorProvider, Toolbar,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough,
    BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting,
    BtnUndo, BtnRedo, Separator,
} from 'react-simple-wysiwyg';

const LANGS = [
    { code: 'nl', short: 'NL', label: 'Dutch'   },
    { code: 'en', short: 'GB', label: 'English' },
    { code: 'fr', short: 'FR', label: 'French'  },
];

/**
 * Reusable single-page editor for About / Disclaimer / Privacy. They all
 * share the same shape in the legacy admin: one fixed page (no list), an
 * iframe toggle, three language tabs with a layout-copy shortcut, and a
 * content area that can be either a WYSIWYG (default for short legal text)
 * or the unlayer designer (for richer About-style pages).
 */
const StaticPageTab = ({
    theme,
    t,
    pageKey,
    defaultTitle,
    defaultDescription,
}) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [config, setConfig] = useState({
        iframe: false,
        editor: 'wysiwyg',                                     // 'wysiwyg' | 'unlayer'
        html:    { nl: '', en: '', fr: '' },
        designs: { nl: null, en: null, fr: null },
    });
    const [langTab, setLangTab] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    const activeLang = LANGS[langTab].code;

    // ESC exits fullscreen — common reflex.
    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const setHtml = (lang, val) => setConfig(p => ({ ...p, html: { ...(p.html || {}), [lang]: val } }));

    const copyLayoutFrom = (fromLang) => {
        if (fromLang === activeLang) return;
        if (config.editor === 'wysiwyg') {
            setHtml(activeLang, config.html?.[fromLang] || '');
        } else {
            const src = config.designs?.[fromLang];
            if (!src) return;
            setConfig(p => ({ ...p, designs: { ...(p.designs || {}), [activeLang]: src } }));
            if (editorRef.current?.editor) {
                try { editorRef.current.editor.loadDesign(src); } catch { /* ignore */ }
            }
        }
    };

    const switchEditor = (mode) => setConfig(p => ({ ...p, editor: mode }));

    // ── unlayer wiring ─────────────────────────────────────────────
    const editorRef = useRef(null);
    const prevLangRef = useRef(activeLang);

    // Persist the current language's design before switching, then load the
    // new language's design into the editor. Same approach as DynamicPageTab.
    useEffect(() => {
        if (config.editor !== 'unlayer') return;
        const ed = editorRef.current?.editor;
        if (!ed) return;
        const prevLang = prevLangRef.current;

        const loadCurrent = () => {
            const design = config.designs?.[activeLang];
            try {
                if (design) ed.loadDesign(design);
                else        ed.loadBlank?.();
            } catch { /* ignore */ }
        };

        if (prevLang !== activeLang) {
            try {
                ed.saveDesign((design) => {
                    setConfig(p => ({ ...p, designs: { ...(p.designs || {}), [prevLang]: design } }));
                    loadCurrent();
                });
            } catch { loadCurrent(); }
        }
        prevLangRef.current = activeLang;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLang, config.editor]);

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {tr(`cfg_static_${pageKey}_title`, defaultTitle)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {tr(`cfg_static_${pageKey}_desc`, defaultDescription)}
                </Typography>
            </Box>

            <Stack spacing={2}>
                {/* Settings */}
                <Section
                    title={tr('cfg_static_settings_title', 'Page settings')}
                    description={tr('cfg_static_settings_desc', 'How the page is rendered on the storefront.')}
                >
                    <Row
                        label={tr('cfg_static_iframe', 'Render the page in an iframe')}
                        help={tr('cfg_static_iframe_help', 'Recommended when the content uses advanced styling or scripts that should not interfere with the rest of the shop.')}
                        control={
                            <Switch
                                color="success" size="small"
                                checked={!!config.iframe}
                                onChange={(e) => setConfig(p => ({ ...p, iframe: e.target.checked }))}
                            />
                        }
                    />
                </Section>

                {/* Content */}
                <Section
                    title={tr('cfg_static_content_title', 'Page content')}
                    description={tr('cfg_static_content_desc', 'The body shown on the page. Each language has its own version — copy from another language with the buttons on the right.')}
                    divider={false}
                >
                    {/* Editor frame — wraps the control row + the editor body
                        in one box. When fullscreen, the wrapper goes fixed so
                        the entire control row travels with it (lang tabs,
                        copy-from buttons, fullscreen toggle). */}
                    {fullscreen && (
                        /* Placeholder so the surrounding layout doesn't
                           collapse while the editor is detached. */
                        <Box sx={{ minHeight: 660, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                {tr('cfg_static_fullscreen_active', 'Editor in fullscreen — press ESC or the exit button to return.')}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={fullscreen ? {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        backgroundColor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        gap: 1.5,
                    } : {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                        {/* Control row — lang tabs left, copy buttons + fullscreen toggle right */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                            <Tabs
                                value={langTab}
                                onChange={(_e, v) => setLangTab(v)}
                                sx={{
                                    minHeight: 36,
                                    '& .MuiTab-root': { minHeight: 36, px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.85rem', minWidth: 0 },
                                    '& .MuiTabs-indicator': { height: 2 },
                                }}
                            >
                                {LANGS.map(l => (
                                    <Tab
                                        key={l.code}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 0.5 }}>{l.short}</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{l.label}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_static_layout_from', 'Copy layout from:')}
                                </Typography>
                                {LANGS.filter((_, i) => i !== langTab).map(l => (
                                    <Button
                                        key={l.code}
                                        size="small" variant="outlined"
                                        onClick={() => copyLayoutFrom(l.code)}
                                        sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 0, px: 1.25 }}
                                    >
                                        {l.label}
                                    </Button>
                                ))}
                                {/* Fullscreen toggle — only meaningful for the unlayer designer */}
                                {config.editor === 'unlayer' && (
                                    <IconButton
                                        size="small"
                                        onClick={() => setFullscreen(v => !v)}
                                        title={fullscreen ? tr('cfg_static_exit_fullscreen', 'Exit fullscreen') : tr('cfg_static_fullscreen', 'Fullscreen')}
                                        sx={{ ml: 0.5 }}
                                    >
                                        {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {/* Editor body — wysiwyg OR unlayer */}
                        {config.editor === 'wysiwyg' ? (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
                                '& .rsw-editor':  { minHeight: 280, border: 'none' },
                                '& .rsw-toolbar': { borderBottom: '1px solid', borderColor: 'divider' },
                                '& .rsw-ce':      { minHeight: 240, padding: '12px 16px' },
                            }}>
                                <EditorProvider>
                                    <SimpleWysiwygEditor
                                        value={config.html?.[activeLang] || ''}
                                        onChange={(e) => setHtml(activeLang, e.target.value)}
                                    >
                                        <Toolbar>
                                            <BtnUndo />
                                            <BtnRedo />
                                            <Separator />
                                            <BtnBold />
                                            <BtnItalic />
                                            <BtnUnderline />
                                            <BtnStrikeThrough />
                                            <Separator />
                                            <BtnNumberedList />
                                            <BtnBulletList />
                                            <Separator />
                                            <BtnLink />
                                            <BtnClearFormatting />
                                        </Toolbar>
                                    </SimpleWysiwygEditor>
                                </EditorProvider>
                            </Box>
                        ) : (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                                overflow: 'hidden',
                                flex: fullscreen ? 1 : 'none',
                                minHeight: 0,
                            }}>
                                <EmailEditor
                                    ref={editorRef}
                                    minHeight={fullscreen ? window.innerHeight - 130 : 600}
                                    onLoad={() => {
                                        const ed = editorRef.current?.editor;
                                        const design = config.designs?.[activeLang];
                                        if (ed && design) {
                                            try { ed.loadDesign(design); } catch { /* ignore */ }
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Editor mode switch — full-width 2-column toggle below the editor.
                        Hidden in fullscreen so the user isn't tempted to switch modes
                        while the unlayer wrapper is detached. */}
                    {!fullscreen && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <EditorModeButton
                                active={config.editor === 'wysiwyg'}
                                onClick={() => switchEditor('wysiwyg')}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                label={tr('cfg_static_use_wysiwyg', 'Visual editor')}
                            />
                            <EditorModeButton
                                active={config.editor === 'unlayer'}
                                onClick={() => switchEditor('unlayer')}
                                icon={<CodeIcon fontSize="small" />}
                                label={tr('cfg_static_use_designer', 'HTML designer')}
                            />
                        </Box>
                    )}
                </Section>
            </Stack>
        </Box>
    );
};
StaticPageTab.propTypes = {
    theme:              PropTypes.string,
    t:                  PropTypes.func,
    pageKey:            PropTypes.oneOf(['about', 'disclaimer', 'privacy', 'intro']).isRequired,
    defaultTitle:       PropTypes.string,
    defaultDescription: PropTypes.string,
};

// ─────────────────────────────────────────────────────────────────────────
// Local helpers — same pattern as BannersTab / DynamicPageTab
// ─────────────────────────────────────────────────────────────────────────

const Section = ({ title, description, children, divider = true }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {description && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{description}</Typography>}
        </Box>
        <Stack
            spacing={1.5}
            divider={divider ? <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} /> : null}
        >
            {children}
        </Stack>
    </Paper>
);
Section.propTypes = { title: PropTypes.node, description: PropTypes.node, children: PropTypes.node, divider: PropTypes.bool };

const Row = ({ label, help, control }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);
Row.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node };

const EditorModeButton = ({ active, onClick, icon, label }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            py: 1.25, borderRadius: 1.5,
            border: '1.5px solid',
            borderColor: active ? 'primary.main' : 'divider',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : '#fff',
            color: active ? 'primary.main' : 'text.primary',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            fontWeight: 600,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main' },
        }}
    >
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </ButtonBase>
);
EditorModeButton.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, icon: PropTypes.node, label: PropTypes.node };

export default StaticPageTab;
